/**
 * Creature Superpowers Game
 * Ages 4-6, Mobile-first, Accessibility-focused
 *
 * Game Flow:
 * 1. Voice Warm-up (microphone permission + voice drawing)
 * 2. Creature Discovery (animated intro with dragon/unicorn/mermaid)
 * 3. YES Game (creative encouragement)
 * 4. Power Selection (16 cards: 8 biotic + 8 abiotic)
 * 5. Creature Naming (3 methods: mix sounds, pick name, type name)
 * 6. Drawing Canvas (face + stickers)
 * 7. Save/Export
 * 8. Visual Survey/Reflection
 */

import { useState } from 'react';
import { GamePhase, GameState, CreatureType, SuperpowerCard } from './types';

// Phase components (to be created)
import VoiceWarmup from './VoiceWarmup';
import CreatureDiscovery from './CreatureDiscovery';
import YesGame from './YesGame';
import PowerSelection from './PowerSelection';
import CreatureNaming from './CreatureNaming';
import CreatureCanvas from './CreatureCanvas';
import SaveExport from './SaveExport';
import CreatureSurvey from './CreatureSurvey';

interface CreatureSuperpowersGameProps {
  language: 'en' | 'es';
  onExit: () => void;
  onLanguageToggle: () => void;
}

export default function CreatureSuperpowersGame({
  language,
  onExit,
  onLanguageToggle
}: CreatureSuperpowersGameProps) {
  const [gameState, setGameState] = useState<GameState>({
    phase: 'intro',
    selectedPowers: [],
    creatureName: '',
    voicePermissionGranted: false,
    hasCompletedWarmup: false
  });
  const [gameStartTime] = useState<number>(Date.now());

  // Phase navigation
  const handlePhaseComplete = (phase: GamePhase, data?: any) => {
    switch (phase) {
      case 'intro':
      case 'voice-warmup':
        setGameState(prev => ({
          ...prev,
          phase: 'creature-discovery',
          hasCompletedWarmup: true,
          voicePermissionGranted: data?.permissionGranted || false
        }));
        break;

      case 'creature-discovery':
        setGameState(prev => ({
          ...prev,
          phase: 'yes-game',
          creature: data.creature
        }));
        break;

      case 'yes-game':
        setGameState(prev => ({
          ...prev,
          phase: 'power-selection'
        }));
        break;

      case 'power-selection':
        setGameState(prev => ({
          ...prev,
          phase: 'creature-naming',
          selectedPowers: data.powers
        }));
        break;

      case 'creature-naming':
        setGameState(prev => ({
          ...prev,
          phase: 'drawing',
          creatureName: data.name
        }));
        break;

      case 'drawing':
        setGameState(prev => ({
          ...prev,
          phase: 'save-export',
          drawingDataURL: data.dataURL
        }));
        break;

      case 'save-export':
        setGameState(prev => ({
          ...prev,
          phase: 'reflection'
        }));
        break;

      case 'reflection':
        // Game complete
        onExit();
        break;
    }
  };

  // Render current phase
  const renderPhase = () => {
    switch (gameState.phase) {
      case 'intro':
      case 'voice-warmup':
        return (
          <VoiceWarmup
            language={language}
            onComplete={(data) => handlePhaseComplete('voice-warmup', data)}
            onSkip={() => handlePhaseComplete('voice-warmup', { permissionGranted: false })}
          />
        );

      case 'creature-discovery':
        return (
          <CreatureDiscovery
            language={language}
            onSelectCreature={(creature: CreatureType) =>
              handlePhaseComplete('creature-discovery', { creature })
            }
          />
        );

      case 'yes-game':
        return (
          <YesGame
            language={language}
            creature={gameState.creature!}
            onComplete={() => handlePhaseComplete('yes-game')}
          />
        );

      case 'power-selection':
        return (
          <PowerSelection
            language={language}
            onComplete={(powers: SuperpowerCard[]) =>
              handlePhaseComplete('power-selection', { powers })
            }
          />
        );

      case 'creature-naming':
        return (
          <CreatureNaming
            language={language}
            creature={gameState.creature!}
            onComplete={(name: string) =>
              handlePhaseComplete('creature-naming', { name })
            }
          />
        );

      case 'drawing':
        return (
          <CreatureCanvas
            language={language}
            creature={gameState.creature!}
            creatureName={gameState.creatureName}
            selectedPowers={gameState.selectedPowers}
            onComplete={(dataURL: string) =>
              handlePhaseComplete('drawing', { dataURL })
            }
          />
        );

      case 'save-export':
        return (
          <SaveExport
            language={language}
            creature={gameState.creature!}
            creatureName={gameState.creatureName}
            selectedPowers={gameState.selectedPowers}
            drawingDataURL={gameState.drawingDataURL!}
            onComplete={() => handlePhaseComplete('save-export')}
          />
        );

      case 'reflection':
        return (
          <CreatureSurvey
            language={language}
            onComplete={() => handlePhaseComplete('reflection')}
            creatureType={gameState.creature}
            creatureName={gameState.creatureName}
            selectedPowers={gameState.selectedPowers}
            gameDurationSeconds={Math.floor((Date.now() - gameStartTime) / 1000)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#fbfbe8' }}>
      {/* Main content */}
      <main className="pb-8">
        {renderPhase()}
      </main>
    </div>
  );
}
